import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { usePublicFacility } from '../context/PublicFacilityContext';
import { initGA4 } from '../utils/ga4';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8002';

/**
 * Tenant portal login — passwordless email-code is the primary path
 * (matches operator login UX). Two-step flow on a single page:
 *   1. Enter email → backend sends 6-digit code via Resend
 *   2. Enter code → backend mints tenant JWT, redirects to dashboard
 *
 * Password is opt-in and never required: rent-now is password-free, and a
 * returning tenant can either keep using email codes or set a password for
 * faster login. "Use password instead" toggles to password login;
 * "First time? Create a password" runs the create-password journey
 * (email → verify code → set password), proving identity via the same
 * one-time code before writing the hash.
 *
 * Anti-enumeration: backend returns the same opaque message whether the
 * email matched a real customer or not. Frontend shows "If that email
 * belongs to a tenant of this facility, a sign-in code has been sent."
 */
const PortalLogin = () => {
  // 'email' (code step 1) | 'code' (code step 2) | 'password' (pw login)
  const [mode, setMode] = useState('email');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  // Create-password journey: when true, a successful code verification
  // routes to the set-password step instead of straight to the dashboard.
  const [creatingPassword, setCreatingPassword] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [loading, setLoading] = useState(false);
  // 60-second resend cooldown — discourages spam without blocking legit retries
  const [resendCooldown, setResendCooldown] = useState(0);
  const codeInputRef = useRef(null);

  // facilityLoading gates the submit buttons: the subdomain->facility
  // lookup is async, and a fast tenant (or an e2e runner) can submit the
  // email before it resolves — landing on a spurious "Facility not
  // detected" error even though the lookup succeeds a beat later.
  const { facility, loading: facilityLoading } = usePublicFacility();
  const navigate = useNavigate();

  useEffect(() => {
    if (facility?.ga4_measurement_id) initGA4(facility.ga4_measurement_id);
  }, [facility]);

  useEffect(() => {
    const token = localStorage.getItem('tenantAccessToken');
    if (token) navigate('/portal/dashboard');
  }, [navigate]);

  // Auto-focus the 6-digit input when we land in code-entry mode.
  useEffect(() => {
    if (mode === 'code' && codeInputRef.current) {
      codeInputRef.current.focus();
    }
  }, [mode]);

  // Countdown timer for the resend button.
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const t = setTimeout(() => setResendCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [resendCooldown]);

  const facilityId = facility?.id || facility?._id;

  const requestCode = async (silentResend = false) => {
    if (!facilityId) {
      setError('Facility not detected. Use your facility subdomain to access the portal.');
      return false;
    }
    setError('');
    if (!silentResend) setInfo('');
    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/tenant/login/request-code`, {
        email: email.trim().toLowerCase(),
        facility_id: facilityId,
      });
      // Backend returns the same opaque message either way. We always
      // advance to the code-entry step; if no code arrives, the tenant
      // either typed the wrong email or isn't on file — they'll
      // re-enter from the "Back" button.
      setMode('code');
      setInfo(
        silentResend
          ? 'New code sent. Check your email.'
          : 'Check your email for a 6-digit sign-in code. It expires in 10 minutes.',
      );
      setResendCooldown(60);
      return true;
    } catch (err) {
      const detail = err.response?.data?.detail || '';
      if (err.response?.status === 429) {
        setError(detail || 'Too many attempts. Please wait 15 minutes and try again.');
      } else {
        setError(detail || 'Something went wrong. Please try again.');
      }
      return false;
    } finally {
      setLoading(false);
    }
  };

  const handleEmailSubmit = (e) => {
    e.preventDefault();
    requestCode(false);
  };

  const handleResend = () => {
    if (resendCooldown > 0) return;
    requestCode(true);
  };

  const handleCodeSubmit = async (e) => {
    e.preventDefault();
    if (!facilityId) {
      setError('Facility not detected.');
      return;
    }
    setError('');
    setLoading(true);
    try {
      const res = await axios.post(`${API_URL}/api/tenant/login/verify-code`, {
        email: email.trim().toLowerCase(),
        facility_id: facilityId,
        code: code.trim(),
      });
      localStorage.setItem('tenantAccessToken', res.data.access_token);
      localStorage.setItem('tenantRefreshToken', res.data.refresh_token);
      localStorage.setItem('tenantCustomer', JSON.stringify(res.data.customer));
      // Create-password journey: the verified code proves identity, so go
      // set the password rather than landing on the dashboard. The tenant
      // is already authenticated (token stored above) for the setup call.
      if (creatingPassword) {
        setCode('');
        setError('');
        setInfo('');
        setMode('setpw');
        return;
      }
      navigate('/portal/dashboard');
    } catch (err) {
      const detail = err.response?.data?.detail || '';
      if (err.response?.status === 429) {
        // Lockout — bounce them back to the email step.
        setError(detail || 'Too many attempts. Please wait 15 minutes and try again.');
        setMode('email');
        setCode('');
      } else {
        setError(detail || 'Invalid or expired code');
      }
    } finally {
      setLoading(false);
    }
  };

  // Create-password journey, final step: write the hash via the existing
  // /setup-password endpoint. The tenant just proved identity with a code,
  // so on success we send them to the dashboard.
  const handleSetPasswordSubmit = async (e) => {
    e.preventDefault();
    if (!facilityId) {
      setError('Facility not detected.');
      return;
    }
    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    setError('');
    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/tenant/setup-password`, {
        email: email.trim().toLowerCase(),
        facility_id: facilityId,
        password: newPassword,
        confirm_password: confirmPassword,
      });
      // Already authenticated from the code step — go straight in.
      navigate('/portal/dashboard');
    } catch (err) {
      const detail = err.response?.data?.detail || '';
      // "Password already set" → they don't need this flow; send them in.
      if (detail.toLowerCase().includes('already set')) {
        navigate('/portal/dashboard');
        return;
      }
      setError(detail || 'Could not set password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Back-compat password login for tenants who set one before the
  // passwordless flow shipped (e.g. the test user Vishal Reddy).
  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    if (!facilityId) {
      setError('Facility not detected.');
      return;
    }
    setError('');
    setLoading(true);
    try {
      const res = await axios.post(`${API_URL}/api/tenant/login`, {
        email: email.trim().toLowerCase(),
        password,
        facility_id: facilityId,
      });
      localStorage.setItem('tenantAccessToken', res.data.access_token);
      localStorage.setItem('tenantRefreshToken', res.data.refresh_token);
      localStorage.setItem('tenantCustomer', JSON.stringify(res.data.customer));
      navigate('/portal/dashboard');
    } catch (err) {
      setError(err.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const goBackToEmail = () => {
    setMode('email');
    setCode('');
    setPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setCreatingPassword(false);
    setError('');
    setInfo('');
  };

  // Start the create-password journey: same email→code identity proof,
  // then route to the set-password step.
  const startCreatePassword = () => {
    setCreatingPassword(true);
    setMode('email');
    setPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setCode('');
    setError('');
    setInfo('Enter your email and we’ll send a code to verify it’s you before you set a password.');
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center"
      style={{ backgroundColor: 'var(--c-bg)' }}
    >
      <div className="max-w-sm w-full">
        <div className="text-center mb-8">
          <h2 className="text-xl font-semibold text-slate-900">
            {facility?.name || 'Tenant Portal'}
          </h2>
          <p className="text-[13px] text-slate-400 mt-1">
            {mode === 'email'
              ? (creatingPassword
                  ? 'Verify your email to create a password'
                  : 'Sign in with a code sent to your email')
              : mode === 'code'
              ? `We sent a code to ${email}`
              : mode === 'setpw'
              ? 'Create a password for faster sign-in'
              : 'Sign in with your password'}
          </p>
        </div>

        <div className="rounded-xl border border-slate-200/80 bg-white p-6">
          {info && (mode === 'code' || mode === 'email') && (
            <div className="mb-4 rounded-lg bg-blue-50 border border-blue-200/80 px-3 py-2.5 text-[13px] text-blue-700">
              {info}
            </div>
          )}
          {error && (
            <div className="mb-4 rounded-lg bg-red-50 border border-red-200/80 px-3 py-2.5 text-[13px] text-red-600">
              {error}
            </div>
          )}

          {/* Step 1: email input */}
          {mode === 'email' && (
            <form onSubmit={handleEmailSubmit} className="space-y-4">
              <div>
                <label className="block text-[13px] font-medium text-slate-700 mb-1.5">
                  Email
                </label>
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-3 py-2 rounded-lg border border-slate-200 text-[13px] text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all"
                  placeholder="you@example.com"
                  disabled={loading}
                  autoComplete="email"
                />
              </div>
              <button
                type="submit"
                disabled={loading || facilityLoading}
                className="w-full py-2 px-4 rounded-lg bg-blue-600 text-white text-[13px] font-medium hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition-colors disabled:opacity-50"
              >
                {loading ? 'Sending code…' : facilityLoading ? 'Loading…' : 'Email me a sign-in code'}
              </button>
            </form>
          )}

          {/* Step 2: 6-digit code */}
          {mode === 'code' && (
            <form onSubmit={handleCodeSubmit} className="space-y-4">
              <div>
                <label className="block text-[13px] font-medium text-slate-700 mb-1.5">
                  6-digit code
                </label>
                <input
                  ref={codeInputRef}
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]{6}"
                  maxLength={6}
                  required
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                  className="w-full px-3 py-3 rounded-lg border border-slate-200 text-center text-2xl font-mono tracking-widest text-slate-900 placeholder:text-slate-300 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all"
                  placeholder="000000"
                  disabled={loading}
                  autoComplete="one-time-code"
                />
              </div>
              <button
                type="submit"
                disabled={loading || code.length !== 6}
                className="w-full py-2 px-4 rounded-lg bg-blue-600 text-white text-[13px] font-medium hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition-colors disabled:opacity-50"
              >
                {loading ? 'Signing in…' : 'Sign in'}
              </button>
              <div className="flex items-center justify-between pt-1">
                <button
                  type="button"
                  onClick={goBackToEmail}
                  className="text-[12px] font-medium text-slate-500 hover:text-slate-700 transition-colors"
                >
                  ← Use a different email
                </button>
                <button
                  type="button"
                  onClick={handleResend}
                  disabled={resendCooldown > 0 || loading}
                  className="text-[12px] font-medium text-blue-600 hover:text-blue-700 disabled:text-slate-400 disabled:cursor-not-allowed transition-colors"
                >
                  {resendCooldown > 0 ? `Resend code in ${resendCooldown}s` : 'Resend code'}
                </button>
              </div>
            </form>
          )}

          {/* Back-compat: password login */}
          {mode === 'password' && (
            <form onSubmit={handlePasswordSubmit} className="space-y-4">
              <div>
                <label className="block text-[13px] font-medium text-slate-700 mb-1.5">
                  Email
                </label>
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-3 py-2 rounded-lg border border-slate-200 text-[13px] text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all"
                  placeholder="you@example.com"
                  disabled={loading}
                  autoComplete="email"
                />
              </div>
              <div>
                <label className="block text-[13px] font-medium text-slate-700 mb-1.5">
                  Password
                </label>
                <input
                  type="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-3 py-2 rounded-lg border border-slate-200 text-[13px] text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all"
                  placeholder="Enter your password"
                  disabled={loading}
                  autoComplete="current-password"
                />
              </div>
              <button
                type="submit"
                disabled={loading || facilityLoading}
                className="w-full py-2 px-4 rounded-lg bg-blue-600 text-white text-[13px] font-medium hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition-colors disabled:opacity-50"
              >
                {loading ? 'Signing in…' : 'Sign in'}
              </button>
            </form>
          )}

          {/* Create-password journey: final step (identity already proven
              via the email code above). */}
          {mode === 'setpw' && (
            <form onSubmit={handleSetPasswordSubmit} className="space-y-4">
              <div>
                <label className="block text-[13px] font-medium text-slate-700 mb-1.5">
                  New password
                </label>
                <input
                  type="password"
                  required
                  minLength={8}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="w-full px-3 py-2 rounded-lg border border-slate-200 text-[13px] text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all"
                  placeholder="At least 8 characters"
                  disabled={loading}
                  autoComplete="new-password"
                />
              </div>
              <div>
                <label className="block text-[13px] font-medium text-slate-700 mb-1.5">
                  Confirm password
                </label>
                <input
                  type="password"
                  required
                  minLength={8}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="w-full px-3 py-2 rounded-lg border border-slate-200 text-[13px] text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all"
                  placeholder="Re-enter your password"
                  disabled={loading}
                  autoComplete="new-password"
                />
              </div>
              <button
                type="submit"
                disabled={loading || newPassword.length < 8 || !confirmPassword}
                className="w-full py-2 px-4 rounded-lg bg-blue-600 text-white text-[13px] font-medium hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition-colors disabled:opacity-50"
              >
                {loading ? 'Saving…' : 'Save password & continue'}
              </button>
            </form>
          )}
        </div>

        <div className="mt-4 text-center space-y-2">
          {/* Password login toggle — hidden during the create-password
              journey and on the set-password step. */}
          {!creatingPassword && mode !== 'setpw' && (
            mode !== 'password' ? (
              <button
                onClick={() => {
                  setMode('password');
                  setError('');
                  setInfo('');
                  setCode('');
                }}
                className="text-[12px] font-medium text-slate-500 hover:text-slate-700 transition-colors"
              >
                Use password instead
              </button>
            ) : (
              <button
                onClick={goBackToEmail}
                className="text-[12px] font-medium text-slate-500 hover:text-slate-700 transition-colors"
              >
                ← Use email code instead
              </button>
            )
          )}

          {/* Create-a-password entry point — shown on the password-login
              screen so tenants who never set one have a way in. */}
          {mode === 'password' && !creatingPassword && (
            <div>
              <button
                onClick={startCreatePassword}
                className="text-[12px] font-medium text-blue-600 hover:text-blue-700 transition-colors"
              >
                First time? Create a password &rarr;
              </button>
            </div>
          )}

          {/* During the create-password journey (email/code/setpw steps),
              offer a way back out to normal sign-in. */}
          {creatingPassword && (
            <button
              onClick={goBackToEmail}
              className="text-[12px] font-medium text-slate-500 hover:text-slate-700 transition-colors"
            >
              ← Back to sign in
            </button>
          )}

          <div className="pt-2 border-t border-slate-200/80">
            <Link
              to="/rent"
              className="text-[13px] font-medium text-blue-600 hover:text-blue-700 transition-colors"
            >
              New here? Rent a unit &rarr;
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PortalLogin;
